import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Motif } from '../../types/motifs'
import { useContextSnackbar } from '../../contexts/snackbarContext'
import { useCurrentOrigin } from '../../utils/useOrigin'
import { useEnv } from '../../utils/useEnv'
import Lightbox from '../Lightbox'
import { useStrapiTranslation } from '../../utils/useStrapiTranslation'
import { cn } from '../../utils/cn'

interface Props {
  motif: Motif
}

const MotifGallerySection = ({ motif }: Props) => {
  const { t } = useTranslation()
  const { strapiTranslate } = useStrapiTranslation()
  const { showSnackbar } = useContextSnackbar()
  const { isKiosk } = useEnv()
  const origin = useCurrentOrigin()

  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [selectedImageIndex, setSelectedImageIndex] = useState(0)

  const thumbnailGalleryRef = useRef<HTMLDivElement>(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)

  // Track which edges of the thumbnail gallery still have hidden content
  const updateScrollIndicators = useCallback(() => {
    const gallery = thumbnailGalleryRef.current
    if (!gallery) return

    const maxScrollLeft = gallery.scrollWidth - gallery.clientWidth
    setCanScrollLeft(gallery.scrollLeft > 1)
    setCanScrollRight(gallery.scrollLeft < maxScrollLeft - 1)
  }, [])

  useEffect(() => {
    const gallery = thumbnailGalleryRef.current
    if (!gallery) return

    gallery.scrollLeft = 0
    updateScrollIndicators()

    const resizeObserver = new ResizeObserver(updateScrollIndicators)
    resizeObserver.observe(gallery)

    return () => resizeObserver.disconnect()
  }, [motif.id, updateScrollIndicators])

  // Create gallery images array from main photo and additional photos
  const galleryImages = [motif.photoUrl, ...(motif.AdditionalPhotos || [])].filter(
    Boolean,
  ) as string[]

  const openLightbox = (index: number = 0) => {
    setSelectedImageIndex(index)
    setLightboxOpen(true)
  }

  const closeLightbox = () => {
    setLightboxOpen(false)
    setSelectedImageIndex(0)
  }

  const goToPrevious = () => {
    const newIndex = selectedImageIndex > 0 ? selectedImageIndex - 1 : galleryImages.length - 1
    setSelectedImageIndex(newIndex)
  }

  const goToNext = () => {
    const newIndex = selectedImageIndex < galleryImages.length - 1 ? selectedImageIndex + 1 : 0
    setSelectedImageIndex(newIndex)
  }

  const selectImage = (index: number) => {
    setSelectedImageIndex(index)
  }

  return (
    <div className="flex flex-col gap-[16px] max-sm:min-w-0">
      <div
        className="flex h-[var(--hd-img)] max-h-[var(--hd-img)] min-h-[var(--hd-img)] w-auto min-w-[var(--hd-img)] max-w-[var(--hd-img-max)] items-center justify-center overflow-hidden rounded-[8px] max-sm:h-auto max-sm:max-h-[unset] max-sm:min-h-[unset] max-sm:w-full max-sm:min-w-full max-sm:max-w-full xxl:flex xxl:h-[var(--fk-img)] xxl:max-h-[var(--fk-img)] xxl:min-h-[var(--fk-img)] xxl:w-auto xxl:min-w-[unset] xxl:max-w-[var(--fk-img-max)] xxl:items-center xxl:justify-center"
        data-id={motif.id}
        data-additional-photos={motif.AdditionalPhotos?.join(',')}
      >
        {motif.photoUrl && (
          <img
            className="block h-[var(--hd-img)] min-h-[var(--hd-img)] w-auto max-w-[var(--hd-img-max)] cursor-pointer object-contain [transition:transform_0.2s_ease] max-sm:h-auto max-sm:max-h-[unset] max-sm:min-h-[unset] max-sm:w-full max-sm:min-w-full max-sm:max-w-full xxl:h-[var(--fk-img)] xxl:max-h-[unset] xxl:min-h-[var(--fk-img)] xxl:w-auto xxl:min-w-[unset] xxl:max-w-[var(--fk-img-max)] xxl:object-contain"
            src={motif.photoUrl}
            alt={motif.title}
            onClick={() => openLightbox(0)}
          />
        )}
      </div>

      <div className="flex flex-row items-center justify-center gap-[8px] whitespace-nowrap xxl:gap-[16px]">
        {/* <span className="artifact-placing">
          <strong>{t('artifactPlacing')} </strong>
        </span> */}
        <span className="font-approach-regular text-[length:var(--hd-inventory-size)] text-black95 xxl:text-[length:var(--fk-inventory-size)]">
          {t('inventoryNumber')}: {motif.inventoryNumber}
        </span>

        {!isKiosk && (
          <span className="relative mx-[4px] my-0 inline-block whitespace-nowrap break-all rounded-[4px] pb-[8px] pl-[36px] pr-[12px] pt-[8px] font-approach-regular text-[length:var(--hd-inventory-size)] text-[#333] no-underline xxl:mx-[8px] xxl:my-0 xxl:inline-block xxl:rounded-[8px] xxl:pb-[12px] xxl:pl-[54px] xxl:pr-[var(--fk-inventory-size)] xxl:pt-[12px] xxl:text-[length:var(--fk-inventory-size)]">
            <button
              className="flex items-center gap-[8px] border-none font-approach-regular text-[length:var(--hd-inventory-size)] text-[#333] [background:none] xxl:gap-[var(--fk-inventory-size)] xxl:text-[length:var(--fk-inventory-size)]"
              onClick={async e => {
                try {
                  const title = String(strapiTranslate(motif, 'title') || '').replace(/\s+/g, '_')
                  await navigator.clipboard.writeText(origin + '/' + motif.id + '_' + title)
                  showSnackbar(t('copyLinkSuccess'))
                } catch {
                  showSnackbar(t('copyLinkFailure'))
                }
              }}
            >
              <img
                src="/icons/message-square-share.svg"
                alt="Share Icon"
                className="h-[calc(var(--hd-inventory-size)*2)] w-[calc(var(--hd-inventory-size)*2)] xxl:h-[calc(var(--fk-inventory-size)*2)] xxl:w-[calc(var(--fk-inventory-size)*2)]"
              />
              {t('copyLink')}
            </button>
          </span>
        )}
      </div>

      <div
        ref={thumbnailGalleryRef}
        className={cn(
          'flex w-full min-w-0 flex-nowrap justify-center gap-[12px] border-none max-sm:justify-start max-sm:overflow-x-auto max-sm:overflow-y-hidden max-sm:px-0 max-sm:py-[4px] max-sm:[--fade-left:0px] max-sm:[--fade-right:0px] max-sm:[-ms-overflow-style:none] max-sm:[-webkit-mask-image:linear-gradient(_to_right,transparent_0,#000_var(--fade-left),#000_calc(100%-var(--fade-right)),transparent_100%_)] max-sm:[-webkit-overflow-scrolling:touch] max-sm:[mask-image:linear-gradient(_to_right,transparent_0,#000_var(--fade-left),#000_calc(100%-var(--fade-right)),transparent_100%_)] max-sm:[scrollbar-width:none] max-sm:[&.can-scroll-left]:[--fade-left:32px] max-sm:[&::-webkit-scrollbar]:hidden max-sm:[&.can-scroll-right]:[--fade-right:32px]',
          { 'can-scroll-left': canScrollLeft, 'can-scroll-right': canScrollRight },
        )}
        onScroll={updateScrollIndicators}
      >
        {galleryImages.slice(1).map((imageUrl, index) => (
          <img
            key={index + 1}
            className="max-h-[90px] w-auto min-w-[60px] flex-[0_0_auto] cursor-pointer rounded-[4px] object-contain shadow-[0_1px_3px_rgba(0,0,0,0.2)] [transition:transform_0.2s_ease] hover:[transform:scale(1.05)] max-sm:first:ml-auto max-sm:last:mr-auto"
            src={imageUrl}
            alt={`${motif.title} - thumbnail ${index + 2}`}
            onClick={() => openLightbox(index + 1)}
            onLoad={updateScrollIndicators}
          />
        ))}
      </div>

      <Lightbox
        isOpen={lightboxOpen}
        onClose={closeLightbox}
        currentImage={galleryImages[selectedImageIndex] || ''}
        images={galleryImages}
        currentIndex={selectedImageIndex}
        onPrevious={goToPrevious}
        onNext={goToNext}
        onSelectImage={selectImage}
        inventoryNumber={motif.inventoryNumber}
        altText={motif.title}
      />
    </div>
  )
}

export default MotifGallerySection
